import React from "react";
import ReactDOMServer from "react-dom/server";
import { createCustomEqual } from "fast-equals";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { useViewState } from "./hooks/useState";

function Map({ onClick, onIdle, children, style, mapid, ...options }) {
  const { translations, locations } = useViewState();
  const ref = React.useRef(null);
  const [map, setMap] = React.useState();
  const infowindow = new window.google.maps.InfoWindow();

  React.useEffect(() => {
    if (ref.current && !map) {
      const config = mapid ? { mapId: mapid } : {};
      setMap(new window.google.maps.Map(ref.current, config));
    }
  }, [ref, map, mapid]);

  React.useEffect(() => {
    if (map) {
      ["click", "idle"].forEach((eventName) =>
        window.google.maps.event.clearListeners(map, eventName)
      );
    }
  }, [map, onIdle]);

  useDeepCompareEffectForMaps(() => {
    if (map) {
      map.setOptions(options);
    }
  }, [map, options]);

  useDeepCompareEffectForMaps(() => {
    if (!map) return;
    const cluster = new MarkerClusterer({
      map,
      renderer: {
        render: ({ count, position }) => {
          const svg = `
            <svg fill="#FF3A30" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240" width="75" height="75">
              <circle cx="120" cy="120" opacity=".95" r="70" />
              <text x="120" y="135" text-anchor="middle" font-size="52" fill="rgba(255,255,255,1.0)" font-family="Arial, sans-serif" font-weight="bold">${count}</text>
            </svg>
          `;

          const parser = new DOMParser();
          const svgElement = parser.parseFromString(svg, "image/svg+xml").documentElement;

          return new window.google.maps.marker.AdvancedMarkerElement({
            map,
            position,
            content: svgElement,
            zIndex: 1000 + count,
          });
        },
      },
    });

    const markers = locations.map((location) => {
      const img = document.createElement('img');
      img.src = "/Static/Previa/assets/icons/map-marker.svg";
      img.width = 32;
      img.height = 32;

      const marker = new window.google.maps.marker.AdvancedMarkerElement({
        map,
        position: { lat: location.lat, lng: location.lng },
        content: img,
        title: location.town,
        gmpClickable: true,
      });

      marker.addListener("gmp-click", () => {
        infowindow.setContent(
          ReactDOMServer.renderToStaticMarkup(
            <InfoWindowContent {...location} />
          )
        );
        infowindow.open({
          map,
          anchor: marker,
        });
      });

      return marker;
    });

    cluster.addMarkers(markers);
  }, [map, locations]);

  const initGeoLocation = () =>
    navigator.geolocation.getCurrentPosition(({ coords }) => {
      // create marker from /Static/Previa/assets/icons/map-marker-current.svg when time is right
      const position = { lat: coords.latitude, lng: coords.longitude };

      infowindow.setPosition(position);
      infowindow.setContent(
        `<h5>${
          translations.yourCurrentPosition || "Din nuvarande position"
        }</h5>`
      );
      infowindow.open({
        map,
        shouldFocus: false,
      });

      map.setZoom(13);
      map.setCenter(position);
    });

  return (
    <div className="o-findOffice__mapContainer">
      <div ref={ref} className="o-findOffice__map" style={style} />
      {options.hideshowmypositionlink != 'true' && (
      <button className="a-link" type="button" onClick={initGeoLocation}>
        {translations.showMyPosition || "Visa min position"}
      </button>
      )}
    </div>
  );
}

const deepCompareEqualsForMaps = createCustomEqual((deepEqual) => (a, b) => {
  if (
    a instanceof window.google.maps.LatLng ||
    b instanceof window.google.maps.LatLng
  ) {
    return new window.google.maps.LatLng(a).equals(
      new window.google.maps.LatLng(b)
    );
  }

  return deepEqual(a, b);
});

function useDeepCompareMemoize(value) {
  const ref = React.useRef();

  if (!deepCompareEqualsForMaps(value, ref.current)) {
    ref.current = value;
  }

  return ref.current;
}

function useDeepCompareEffectForMaps(callback, dependencies) {
  React.useEffect(callback, dependencies.map(useDeepCompareMemoize));
}

function InfoWindowContent({
  name,
  address,
  zip,
  town,
  formatted_phone_number: phone,
  link,
  moreInfo,
}) {
  return (
    <div className="o-findOffice__infoWindow">
      <h3>{name}</h3>
      <address>
        {address}
        <br />
        {zip} {town}
        <br />
        <a href={`tel:${phone}`}>{phone}</a>
        <br />
        <a href={link}>{moreInfo || "Mer info"}</a>
      </address>
    </div>
  );
}

export default Map;
